import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import {
  createUserEndPointV3,
  deleteUserEndPointV3,
  disableUserEndPointV3,
  driverProfileEndpoint,
  enableUserEndPointV3,
  getGroupsEndPointV3,
  getUsersEndPointV3,
  updateUserEndPointV3,
  updateUserGroupEndPointV3,
  userProfileEndpoint,
  userProfileEndpointV3,
  addUserToGroupEndPointV3,
  rmvUserToGroupEndPointV3,
  getDriverProfileV3
} from '../constants/endpoints';
import {
  CREATE_USER_ERROR,
  CREATE_USER_INVALID_MAIL,
  CREATE_USER_SUCCESS,
  DELETE_DISABLE_USER_ERROR,
  DELETE_USER_SUCCESS,
  DISABLE_USER_SUCCESS,
  ENABLE_USER_SUCCESS,
  GENERAL_USERS,
  GET_GROUPS_SUCCESS,
  GET_USERS_SUCCESS,
  GET_USR_BY_GROUP_SUCCESS,
  SEARCH_USERS_SUCCESS,
  UNEXPECTED_ERROR,
  UPDATE_USER_SUCCESS,
  UPDATE_USR_GROUP_SUCCESS,
  USER_GROUP_ERROR,
  USER_PROFILE_SUCCESS,
  UPDATE_ME_PROFILE_SUCCESS,
  USER_ME_PROFILE_SUCCESS,
  ADD_USR_TO_GROUP_SUCCESS,
  RMV_USR_TO_GROUP_SUCCESS
} from '../constants/services';
import CustomError from '../utils/CustomError';
import fetch from '../utils/fetch';
import { normalizeDriverUserProfile } from '../utils/users';

import { USER_PAGE_SIZE } from '../constants';

export async function getUserProfile(token, username, options = {}) {
  const { lowercaseUsername = true } = options;
  try {
    const normalizedUsername = username
      ? lowercaseUsername
        ? username.toLowerCase()
        : username
      : null;
    const params = normalizedUsername ? { username: normalizedUsername } : null;
    const query = await fetch(userProfileEndpointV3, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const groups = get(json, 'data.attributes.groups', []);

    let dataFormatted;
    if (username) {
      dataFormatted = {
        ...json.data,
        groups: json.data.attributes.groups,
        attributes: {
          ...json.data.attributes,
          groups: [
            groups.length ? json.data.attributes.groups[0] : GENERAL_USERS
          ]
        },
        customAtributes: { ...json.data.attributes }
      };
    } else {
      dataFormatted = {
        ...json.data,
        attributes: {
          ...json.data
        },
        customAtributes: { ...json.data }
      };
    }

    return {
      status: USER_PROFILE_SUCCESS,
      data: dataFormatted
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getUserProfileV3(token, username) {
  try {
    const params = username ? { username } : null;
    const query = await fetch(userProfileEndpoint, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    const dataFormatted = {
      ...json.data,
      attributes: {
        ...json.data.profile
      },
      customAtributes: { ...json.data.profile }
    };

    return {
      status: USER_PROFILE_SUCCESS,
      data: dataFormatted
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getUsers(token, paginationToken, groupName, limit) {
  const endpoint = getUsersEndPointV3;
  const status = groupName ? GET_USR_BY_GROUP_SUCCESS : GET_USERS_SUCCESS;
  try {
    const params = {
      limit: limit || USER_PAGE_SIZE,
      groupName,
      ...(paginationToken ? { 'pagination-token': paginationToken } : {})
    };
    const query = await fetch(endpoint, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status,
      data: json.data,
      info: json.info
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getUsersByCompany(token, companyName) {
  const endpoint = getUsersEndPointV3;
  try {
    const params = { company: companyName };
    const query = await fetch(endpoint, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: GET_USERS_SUCCESS,
      data: json.data,
      info: json.info
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getGroups(token) {
  const endpoint = getGroupsEndPointV3;
  try {
    const query = await fetch(endpoint, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: GET_GROUPS_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function createUser(token, data) {
  const endpoint = createUserEndPointV3;
  const { username, password, email, customAtributes } = data;
  const { 'custom:companyId': companyId } = customAtributes;
  const validEmailRegex = RegExp(
    /^([a-zA-Z0-9_\-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/i
  );
  const isValidMail = validEmailRegex.test(email);
  if (!username || !password || !companyId) {
    throw new CustomError(CREATE_USER_ERROR);
  }
  if (!isValidMail) {
    throw new CustomError(CREATE_USER_INVALID_MAIL);
  }
  try {
    const query = await fetch(endpoint, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: CREATE_USER_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function deleteUser(token, username) {
  const endpoint = deleteUserEndPointV3;
  if (!username) {
    throw DELETE_DISABLE_USER_ERROR;
  }
  try {
    const query = await fetch(endpoint, {
      method: 'delete',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ username })
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: DELETE_USER_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function disableUser(token, username) {
  const endpoint = disableUserEndPointV3;
  if (!username) {
    throw DELETE_DISABLE_USER_ERROR;
  }
  try {
    const query = await fetch(endpoint, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ username })
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: DISABLE_USER_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function enableUser(token, username) {
  const endpoint = enableUserEndPointV3;
  if (!username) {
    throw DELETE_DISABLE_USER_ERROR;
  }
  try {
    const query = await fetch(endpoint, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ username })
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: ENABLE_USER_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function addUserToGroup(token, username, group) {
  const endpoint = addUserToGroupEndPointV3;
  if (!username || !group) {
    throw USER_GROUP_ERROR;
  }
  try {
    const query = await fetch(endpoint, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ username, group })
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: ADD_USR_TO_GROUP_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function rmvUserToGroup(token, username, group) {
  const endpoint = rmvUserToGroupEndPointV3;
  if (!username || !group) {
    throw USER_GROUP_ERROR;
  }
  try {
    const query = await fetch(endpoint, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ username, group })
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: RMV_USR_TO_GROUP_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

/**
 * replaceUserGroup function to replace current user group for a new one
 * * @param {string} token to validate endpoint access
 * * @param {string} username from user to change group
 * * @param {string} grupo new group
 * * @returns {void}
 * */
export async function replaceUserGroup(token, username, group) {
  if (!username || !group) {
    throw USER_GROUP_ERROR;
  }
  try {
    const query = await fetch(updateUserGroupEndPointV3, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ username, group })
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: UPDATE_USR_GROUP_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function updateUser(token, data) {
  const endpoint = updateUserEndPointV3;
  try {
    const { customAtributes, username } = data;
    const enabled = get(data, 'enabled', true);
    const { groups } = customAtributes || [];
    const userData = pickBy(
      {
        username,
        name: customAtributes.name,
        phone: customAtributes.phone,
        company: customAtributes.companyName,
        companyId: customAtributes.companyId,
        enabled: enabled ? 'true' : 'false',
        group: groups[0]
      },
      (value, key) =>
        key === 'companyId' && !Number.isInteger(value) ? null : value
    );

    if (customAtributes.phone === '') {
      userData.phone = '';
    }
    const query = await fetch(endpoint, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    return {
      status: UPDATE_USER_SUCCESS
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function createUserV3(token, userData) {
  try {
    const query = await fetch(createUserEndPointV3, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: CREATE_USER_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function updateUserV3(token, userData) {
  try {
    const query = await fetch(updateUserEndPointV3, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    return {
      status: UPDATE_USER_SUCCESS
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function searchUsers(token, param, box = 'qusername') {
  const endpoint = getUsersEndPointV3;
  try {
    const status = SEARCH_USERS_SUCCESS;
    const params = { [box]: param };
    const query = await fetch(endpoint, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status,
      data: json.data,
      info: json.info
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}
/**
 * getDriverProfile to get driver profile from backend
 * * @param {string} token is a valid api token
 * * @param {string} qusername optional param to get data from username
 * * @returns {object} result driver user data
 * */
export async function getDriverProfile(token) {
  let rawBody = null;
  try {
    const query = await fetch(getDriverProfileV3, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      }
    });
    rawBody = await query.text();
    const transactionId = query.headers.get('transactionId');
    const json = rawBody ? JSON.parse(rawBody) : {};
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: USER_PROFILE_SUCCESS,
      data: normalizeDriverUserProfile(json.data)
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

/**
 * getUsersByGroup function to get users by group
 * @param {string} token param required
 * @param {string} groupName param required
 * @returns {array} return and array of users
 */
export async function getUsersByGroup(token, groupName) {
  const status = groupName ? GET_USR_BY_GROUP_SUCCESS : GET_USERS_SUCCESS;
  try {
    const params = { groupName };
    const query = await fetch(getUsersEndPointV3, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getMeProfile(token) {
  try {
    const query = await fetch(`${userProfileEndpoint}/me`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: USER_ME_PROFILE_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function updateMeProfile(token, data) {
  try {
    const endpoint = `${userProfileEndpoint}/me`;
    const query = await fetch(endpoint, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      status: UPDATE_ME_PROFILE_SUCCESS,
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }

  throw new CustomError(UNEXPECTED_ERROR);
}
